import { useState, useEffect, useRef } from "react";
import { useSEO } from "@/hooks/use-seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import {
  BookOpen, CheckCircle, Shield, TrendingUp, Heart, FileText,
  RefreshCw, Eye, Star, Users, BarChart2, Lock, Zap, Calendar,
  ChevronDown
} from "lucide-react";

const METHODOLOGY_STEPS = [
  {
    n: "01",
    icon: CheckCircle,
    title: "Registrace a ověření identity (KYC)",
    desc: "Testování každého kasina začíná reálnou registrací. Náš recenzent prochází celým procesem vytvoření účtu jako běžný hráč — od vyplnění registračního formuláře přes ověření e-mailu až po plný KYC proces (nahrání dokladů totožnosti). Hodnotíme rychlost ověření, přehlednost procesu a komunikaci kasina se zákazníkem.",
    criteria: ["Délka a složitost registrace", "Rychlost a transparentnost KYC procesu", "Srozumitelnost požadavků na dokumentaci"],
  },
  {
    n: "02",
    icon: TrendingUp,
    title: "Analýza bonusové nabídky",
    desc: "Bonusy jsou jedním z nejčastěji zavádějících aspektů online kasin. Proto každou bonusovou nabídku analyzujeme do hloubky — nejen výši bonusu, ale zejména podmínky protočení (wagering requirements), časové limity, seznam povolených her a maximální výběr z bonusu. Přepočítáváme reálnou hodnotu bonusu pro hráče.",
    criteria: ["Podmínky protočení (wagering)", "Maximální výběr z bonusu", "Časový limit na splnění podmínek", "Hry zahrnuté do protočení", "Reálná hodnota vs. marketingová komunikace"],
  },
  {
    n: "03",
    icon: BarChart2,
    title: "Testování platebních metod",
    desc: "Každou dostupnou platební metodu fyzicky testujeme — provádíme skutečný vklad i výběr. Měříme dobu zpracování od zadání požadavku po přijetí prostředků. Hodnotíme poplatky, limity, dostupnost pro české hráče a přehlednost platebního rozhraní.",
    criteria: ["Rychlost vkladů a výběrů", "Poplatky za transakce", "Minimální a maximální limity", "Dostupnost v CZK / bez poplatků za konverzi", "Šíře nabídky metod"],
  },
  {
    n: "04",
    icon: Shield,
    title: "Bezpečnost a licence",
    desc: "Doporučujeme výhradně kasina s platnou licencí od renomovaného regulátora. Ověřujeme platnost licence, historii operátora a případné stížnosti hráčů. Kontrolujeme technická bezpečnostní opatření (SSL certifikát, šifrování dat, ochrana hráčských fondů). Férovost her ověřují nezávislé organizace jako eCOGRA (ecogra.org).",
    criteria: ["Platnost a druh licence (MGA, UKGC, Curaçao...)", "Historie operátora a případné sankce", "SSL šifrování a bezpečnost dat", "Oddělení hráčských fondů od provozního kapitálu"],
  },
  {
    n: "05",
    icon: Heart,
    title: "Zodpovědné hraní",
    desc: "Tato oblast je pro nás nepřekročitelnou podmínkou. Kasino musí nabízet nástroje pro zodpovědné hraní — nastavení limitů vkladů, časová omezení, možnost dočasné pauzy i trvalého sebeloučení. Hodnotíme dostupnost a srozumitelnost těchto nástrojů pro hráče.",
    criteria: ["Limity vkladů a sázek", "Možnost časové pauzy a sebeloučení", "Přístup k historii transakcí a hry", "Spolupráce s GamCare nebo podobnými organizacemi", "Věkové ověření"],
  },
  {
    n: "06",
    icon: Star,
    title: "Herní nabídka a software",
    desc: "Testujeme šíři a kvalitu herní nabídky — sloty, stolní hry, live casino, jackpoty. Hodnotíme poskytovatele softwaru (NetEnt, Evolution, Pragmatic Play atd.), rychlost načítání, mobilní kompatibilitu a průběžnou aktualizaci herního portfolia.",
    criteria: ["Počet a rozmanitost her", "Renomé poskytovatelů softwaru", "Mobilní kompatibilita", "Rychlost načítání a stabilita platformy"],
  },
  {
    n: "07",
    icon: Users,
    title: "Zákaznická podpora",
    desc: "Testujeme zákaznický servis v reálných situacích — odesíláme dotazy přes live chat, e-mail i případné další kanály. Měříme dobu odezvy, odbornost odpovědí a ochotu pomoci. Dostupnost podpory v češtině je bonusovým hodnotícím faktorem.",
    criteria: ["Doba odezvy (live chat, e-mail)", "Odbornost a přesnost odpovědí", "Dostupnost v češtině nebo slovenštině", "Otevírací hodiny podpory"],
  },
  {
    n: "08",
    icon: FileText,
    title: "Redakce, podpis a publikace",
    desc: "Dokončená recenze prochází interní redakcí — druhý člen týmu text kontroluje z pohledu přesnosti, srozumitelnosti a dodržení redakčních standardů. Po schválení je recenze podepsána konkrétním autorem a publikována. Datum první publikace i datum aktualizace jsou vždy viditelné.",
    criteria: ["Interní peer review před publikací", "Podpis konkrétního autora", "Datum publikace a aktualizace", "Kontrola faktu a přesnosti dat"],
  },
];

const UPDATE_POLICY = [
  { icon: RefreshCw, title: "Průběžná aktualizace", desc: "Recenze jsou revidovány minimálně jednou za 6 měsíců nebo při každé podstatné změně — bonusy, podmínky, platební metody, licence." },
  { icon: Eye, title: "Monitoring změn", desc: "Sledujeme změny v podmínkách kasin, licenčních databázích regulátorů a herních portfoliích. Při zjištění rozdílu je obsah aktualizován neprodleně." },
  { icon: Zap, title: "Reakce na stížnosti", desc: "Pokud obdržíme od čtenářů upozornění na neaktuální nebo nepřesnou informaci, prověřujeme ji prioritně a v případě potvrzení okamžitě opravujeme." },
  { icon: Lock, title: "Archiv verzí", desc: "Každá podstatná aktualizace recenze je interně zaznamenána s datem a popisem změn. Hráči mohou vidět datum poslední revize přímo na stránce recenze." },
];

function StepNavSidebar({ activeStep }: { activeStep: number }) {
  return (
    <nav className="hidden lg:block sticky top-24 self-start w-64 flex-shrink-0" data-testid="nav-step-sidebar">
      <div className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-4">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-2">Přejít na krok</h3>
        <div className="space-y-0.5">
          {METHODOLOGY_STEPS.map((step, i) => (
            <a
              key={step.n}
              href={`#step-${step.n}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                activeStep === i
                  ? "bg-[#003da5]/10 text-[#003da5] font-bold"
                  : "text-gray-600 hover:bg-[#f3f6fb] hover:text-gray-900"
              }`}
              data-testid={`nav-step-${step.n}`}
            >
              <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0 ${
                activeStep === i
                  ? "bg-[#003da5] text-white"
                  : "bg-[#f3f6fb] text-gray-400"
              }`}>
                {step.n}
              </span>
              <span className="truncate">{step.title.split(" ").slice(0, 3).join(" ")}</span>
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}

function MobileStepNav({ activeStep }: { activeStep: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="lg:hidden sticky top-16 z-20 mb-6" data-testid="nav-step-mobile">
      <button
        onClick={() => setOpen(!open)}
        className="w-full bg-white rounded-xl border border-[#dde4ee] shadow-sm px-4 py-3 flex items-center justify-between text-sm font-semibold text-gray-700"
        data-testid="btn-jump-to-step"
      >
        <span>Krok {METHODOLOGY_STEPS[activeStep]?.n}: {METHODOLOGY_STEPS[activeStep]?.title.split(" ").slice(0, 3).join(" ")}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-white rounded-xl border border-[#dde4ee] shadow-lg z-30 overflow-hidden">
          {METHODOLOGY_STEPS.map((step, i) => (
            <a
              key={step.n}
              href={`#step-${step.n}`}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                activeStep === i
                  ? "bg-[#003da5]/10 text-[#003da5] font-bold"
                  : "text-gray-600 hover:bg-[#f3f6fb]"
              } ${i < METHODOLOGY_STEPS.length - 1 ? "border-b border-[#f0f0f0]" : ""}`}
              data-testid={`mobile-nav-step-${step.n}`}
            >
              <span className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-black flex-shrink-0 ${
                activeStep === i ? "bg-[#003da5] text-white" : "bg-[#f3f6fb] text-gray-400"
              }`}>
                {step.n}
              </span>
              {step.title}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RedakcniMetodologiePage() {
  useSEO({ title: "Redakční Metodologie – Jak testujeme a hodnotíme kasina | HrajBonus", description: "Zjistěte, jak HrajBonus testuje a hodnotí online kasina. 15+ kritérií: licence, bonusy, výplaty, podpora. 100% nezávislé hodnocení. 18+",
  url: "https://hrajbonus.com/redakcni-metodologie", });

  const [activeStep, setActiveStep] = useState(0);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          const topEntry = visible.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          const idx = stepRefs.current.findIndex(ref => ref === topEntry.target);
          if (idx !== -1) setActiveStep(idx);
        }
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: 0.1 }
    );

    stepRefs.current.forEach(ref => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f8fc]">
      <Header />

      <div
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }}
      >
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 25% 50%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-16 sm:py-20 relative z-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-white/60 text-sm font-semibold uppercase tracking-widest">Transparentnost</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5 max-w-2xl">
            Naše redakční metodologie
          </h1>
          <p className="text-white/65 text-lg leading-relaxed max-w-2xl">
            Věříme, že důvěra se buduje transparentností. Proto vám detailně vysvětlujeme, jak každou recenzi kasina připravujeme, testujeme a aktualizujeme — a proč na naše hodnocení můžete spoléhat.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5" data-testid="badge-updated-methodology">
            <Calendar className="w-3.5 h-3.5 text-white/70" />
            <span className="text-white/80 text-xs font-semibold">Aktualizováno: 12. března 2026</span>
          </div>
        </div>
      </div>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-20 flex-1 space-y-16">

        <section>
          <div className="bg-white rounded-3xl border border-[#dde4ee] shadow-sm p-8 sm:p-12">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
                  <Eye className="w-3.5 h-3.5" /> Náš přístup
                </span>
                <h2 className="text-2xl font-black text-gray-900 mb-4">Recenze z pohledu reálného hráče</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Na hrajbonus.com nepřijímáme recenze od kasin, nepoužíváme obsah psaný na objednávku operátorů a nepublikujeme hodnocení bez přímého testování. Každá recenze vzniká fyzickým testováním platformy — od registrace přes vklad, hru až po výběr výhry.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Affiliate spolupráce je součástí našeho obchodního modelu, ale nikdy neovlivňuje obsah recenzí. Kasina, se kterými spolupracujeme, jsou hodnocena naprosto stejnými standardy jako ostatní. Negativní hodnocení nepodléhají cenzuře.
                </p>
              </div>
              <div className="space-y-3">
                {[
                  "Žádná platba za pozitivní hodnocení",
                  "Každé kasino fyzicky otestováno",
                  "Každá recenze podepsána autorem",
                  "Pravidelné aktualizace obsahu",
                  "Transparentní affiliate disclosure",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2.5 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0" />
                    {item}
                  </div>
                ))}
                <div className="mt-4 pt-4 border-t border-[#eef1f7] space-y-2">
                  <p className="text-gray-500 text-xs leading-relaxed">
                    Férovost her ověřujeme prostřednictvím nezávislých certifikací od organizací jako{" "}
                    <a href="https://www.ecogra.org/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-ecogra">eCOGRA</a>{" "}
                    a licence kontrolujeme u regulátorů jako{" "}
                    <a href="https://www.mga.org.mt/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-mga">Malta Gaming Authority (MGA)</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="mb-10">
            <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
              <BookOpen className="w-3.5 h-3.5" /> Proces recenze
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-3">8 kroků každé recenze kasina</h2>
            <p className="text-gray-600 max-w-2xl leading-relaxed">
              Každá recenze prochází standardizovaným procesem, který zajišťuje konzistentní hloubku, přesnost a férové hodnocení.
            </p>
          </div>

          <MobileStepNav activeStep={activeStep} />

          <div className="flex gap-10">
            <StepNavSidebar activeStep={activeStep} />

            <div className="flex-1 relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#003da5] via-[#003da5]/40 to-[#003da5]/10 hidden sm:block" />

              <div className="space-y-0">
                {METHODOLOGY_STEPS.map((step, i) => {
                  const Icon = step.icon;
                  return (
                    <div
                      key={step.n}
                      id={`step-${step.n}`}
                      ref={el => { stepRefs.current[i] = el; }}
                      className="relative pl-0 sm:pl-16 pb-10 scroll-mt-28"
                      data-testid={`step-${step.n}`}
                    >
                      <div className="hidden sm:flex absolute left-0 top-0 w-12 h-12 rounded-2xl items-center justify-center text-white text-lg font-black shadow-md z-10"
                        style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}
                      >
                        {step.n}
                      </div>

                      <div className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                        <div className="p-6 sm:p-8">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="sm:hidden w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-black"
                              style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}
                            >
                              {step.n}
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-[#eef3ff] flex items-center justify-center">
                              <Icon className="w-5 h-5 text-[#003da5]" />
                            </div>
                            <h3 className="text-lg font-black text-gray-900">{step.title}</h3>
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed mb-5">{step.desc}</p>
                          <div>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wide block mb-2">Hodnotíme</span>
                            <div className="flex flex-wrap gap-2">
                              {step.criteria.map((c) => (
                                <span key={c} className="text-xs px-3 py-1.5 rounded-full bg-[#f3f6fb] border border-[#dde4ee] text-gray-600 font-medium">
                                  {c}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
              <RefreshCw className="w-3.5 h-3.5" /> Aktualizace
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-3">Politika aktualizací obsahu</h2>
            <p className="text-gray-600 max-w-2xl leading-relaxed">
              Online kasina se mění — bonusy expirují, platební metody přibývají, licence se obnovují. Proto jsou naše recenze živé dokumenty, nikoli statické texty.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {UPDATE_POLICY.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">{item.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <a
              href="/nasi-autori"
              className="group bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-4"
              data-testid="cta-authors"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#003da5] transition-colors">Poznejte náš tým</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Kdo stojí za každou recenzí — naši autoři a jejich odbornost.</p>
              </div>
            </a>
            <a
              href="/o-nas"
              className="group bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-4"
              data-testid="cta-about"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                <Eye className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#003da5] transition-colors">O nás</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Naše mise, hodnoty a proč se zaměřujeme na český trh.</p>
              </div>
            </a>
            <a
              href="/kontakt"
              className="group bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-4 sm:col-span-2 lg:col-span-1"
              data-testid="cta-contact"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#003da5] transition-colors">Nahlásit nepřesnost</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Našli jste chybu v recenzi? Dejte nám vědět.</p>
              </div>
            </a>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
